/**
 * The answer side of a drill view session (moved verbatim from StepView.vue):
 * the try timer, the unmount guard (BUG-24), the question text / drill result
 * the cards report, then audio → feedback dialogs → answer flow.
 */
import {
  computed,
  onBeforeUnmount,
  ref,
  toValue,
  type MaybeRefOrGetter,
} from 'vue';
import { useTimer } from 'shared/composables/useTimer/useTimer';
import type {
  DrillDrillType,
  DrillTimeLimitSettings,
} from 'src/types/course/drill';
import { isDrillKind } from '../utilities/drillStrategies/resolveDrillKind';
import { drillContextOf, getDrillStrategy } from '../utilities/drillStrategies';
import type {
  DrillItemMap,
  DrillKind,
} from '../utilities/drillStrategies/types';
import { useDrillAudio } from './useDrillAudio';
import { useDrillFeedbackDialogs } from './useDrillFeedbackDialogs';
import { useDrillTimeLimit } from './useDrillTimeLimit';
import {
  useAnswerFlow,
  type AnswerFlowSettings,
  type UseAnswerFlowOptions,
} from './useAnswerFlow';

/** The engine (useStep) fields the session reads. */
export interface DrillSessionEngine extends Pick<
  UseAnswerFlowOptions,
  | 'checkAnswer'
  | 'updateProgress'
  | 'answerText'
  | 'getFeedbackSubtitle'
  | 'getFeedbackAnswer'
  | 'getCorrectFeedbackAnswer'
  | 'resetStepData'
> {
  /** The resolved step kind (a Filter step as its real kind, D-5). */
  filterStepType: MaybeRefOrGetter<string | null | undefined>;
  stepDrillProps: MaybeRefOrGetter<
    | {
        isQuestionHearingEnabled?: boolean;
        drillSettings?: AnswerFlowSettings & {
          time_limit?: DrillTimeLimitSettings | null;
        };
        drillType?: DrillDrillType;
      }
    | undefined
  >;
  isCompletedStep: MaybeRefOrGetter<boolean>;
  currentStackItem: MaybeRefOrGetter<unknown>;
  currentCorrectAnswer: MaybeRefOrGetter<unknown>;
}

export interface UseDrillSessionOptions {
  engine: DrillSessionEngine;
}

export function useDrillSession({ engine }: UseDrillSessionOptions) {
  const {
    timeMiliseconds,
    formattedTime,
    start: timerStart,
    stop: timerStop,
    reset: timerReset,
  } = useTimer();

  // The RESOLVED drill kind (a Filter step behaves like its kind, D-5).
  const drillKind = computed(() => {
    const kind = toValue(engine.filterStepType);
    return isDrillKind(kind) ? kind : null;
  });
  const strategy = computed(() => getDrillStrategy(drillKind.value));

  const drillTextContent = ref<string>('');

  // Nothing of an answer flow may run after the view is gone (BUG-24).
  let isUnmounted = false;
  onBeforeUnmount(() => {
    isUnmounted = true;
  });
  const isActive = () => !isUnmounted;

  const drillResult = ref();

  function handleDrillResult(result: unknown) {
    drillResult.value = result;
  }

  const audio = useDrillAudio({
    strategy,
    isQuestionHearingEnabled: () =>
      toValue(engine.stepDrillProps)?.isQuestionHearingEnabled,
    isCompletedStep: engine.isCompletedStep,
    isActive,
    timer: { start: timerStart, reset: timerReset },
    questionText: drillTextContent,
  });

  const drillSettings = () => toValue(engine.stepDrillProps)?.drillSettings;

  const dialogs = useDrillFeedbackDialogs({
    drillSettings,
    onDismiss: (kind) => {
      audio.presentQuestion().catch((error) => {
        console.error(
          `Error playing question after ${kind} dialog hide:`,
          error,
        );
      });
    },
  });

  const flow = useAnswerFlow({
    checkAnswer: engine.checkAnswer,
    updateProgress: engine.updateProgress,
    answerText: engine.answerText,
    getFeedbackSubtitle: engine.getFeedbackSubtitle,
    getFeedbackAnswer: engine.getFeedbackAnswer,
    getCorrectFeedbackAnswer: engine.getCorrectFeedbackAnswer,
    resetStepData: engine.resetStepData,
    helpAnswer: () =>
      (strategy.value
        ? strategy.value.helpAnswer(
            toValue(engine.currentStackItem) as DrillItemMap[DrillKind],
            drillContextOf(toValue(engine.stepDrillProps)?.drillType),
          )
        : toValue(engine.currentCorrectAnswer)) as ReturnType<
        UseAnswerFlowOptions['helpAnswer']
      >,
    drillSettings,
    audio,
    dialogs,
    timer: {
      timeMiliseconds,
      start: timerStart,
      stop: timerStop,
      reset: timerReset,
    },
    isActive,
    questionText: drillTextContent,
  });

  // Speed Round: a timeout is a wrong try (no-op without a time limit).
  const timeLimit = useDrillTimeLimit({
    timeLimit: () => drillSettings()?.time_limit,
    elapsedMs: timeMiliseconds,
    questionAudioMs: audio.questionAudioMs,
    isSpeakingQuestion: audio.isSpeakingQuestion,
    canTimeout: () =>
      isActive() &&
      !flow.isCheckingAnswer.value &&
      !toValue(engine.isCompletedStep),
    onTimeout: () => flow.handleTimeout(),
    formattedTime,
  });

  return {
    timeMiliseconds,
    formattedTime,
    timerStart,
    timerStop,
    timerReset,
    isActive,
    drillKind,
    strategy,
    drillTextContent,
    drillResult,
    handleDrillResult,
    ...audio,
    ...flow,
    ...timeLimit,
  };
}

export type UseDrillSessionReturn = ReturnType<typeof useDrillSession>;
